import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import DropdownButton from '../basics/DropdownButton';
import ExportCSVProjectListButton from './ExportCsvProjectListButton';
import { MULTI_YEAR } from '../../constants/years';
import {
  ExportDashboardGeneticStockBasisButton,
  ExportDashboardInputsBasisButton,
  ExportDashboardOperationsBasisButton,
  ExportDashboardSiteInfrastructureBasisButton,
} from './ExportDashboardCostBasisButton';
import { useUserRole } from '../../utils/hooks';
import YearSelect from '../YearSelect';
import { useMultiProjectDashboardContext } from '../../contexts/MultiProjectDashboardContext';
import ExportDashboardCarbonButton from './ExportDashboardCarbonButton';
import ExportDashboardYieldButton from './ExportDashboardYieldButton';
import ExportDashboardRevenueButton from './ExportDashboardRevenueButton';
import ExportDashboardCropsButton from './ExportDashboardCropsButton';

const overviewCategoryOptions = [
  {
    name: 'Project list',
    value: 'projectList',
  },
  { name: 'Crops', value: 'crops' },
];

const econCategoryOptions = [
  {
    name: 'Yield',
    value: 'yield',
  },
  {
    name: 'Carbon',
    value: 'carbon',
  },
  {
    name: 'Revenue',
    value: 'revenue',
  },
];

const costBasisCategoryOptions = [
  {
    name: 'Genetic Stock Cost Basis',
    value: 'geneticStockCostBasis',
    superadminOnly: true,
  },
  {
    name: 'Site Infrastructure Cost Basis',
    value: 'siteInfraCostBasis',
    superadminOnly: true,
  },
  {
    name: 'Operations Cost Basis',
    value: 'operationsCostBasis',
    superadminOnly: true,
  },
  {
    name: 'Inputs Cost Basis',
    value: 'inputsCostBasis',
    superadminOnly: true,
  },
];

const categoryOptions = [
  { name: 'Overview', header: true },
  ...overviewCategoryOptions,
  { name: 'Economics', header: true },
  ...econCategoryOptions,
  { name: 'Cost Basis', header: true },
  ...costBasisCategoryOptions,
];

const projectListFiletypeOptions = [{ name: 'CSV', value: 'csv' }];

const multiYearOptions = [
  { name: '5 Year', value: { year: MULTI_YEAR, years: 5 } },
  { name: '10 Year', value: { year: MULTI_YEAR, years: 10 } },
  { name: '15 Year', value: { year: MULTI_YEAR, years: 15 } },
  { name: '20 Year', value: { year: MULTI_YEAR, years: 20 } },
  { name: '30 Year', value: { year: MULTI_YEAR, years: 30 } },
];

const singleYearOptions = (startYear) =>
  Array.from({ length: 30 }, (_, i) => {
    const year = startYear + i;
    return { name: year.toString(), value: { year, years: 1 } };
  });

const multiOrSingleYearOptions = (startYear) => [
  { name: 'Multi year', header: true },
  ...multiYearOptions,
  { name: 'Single year', header: true },
  ...singleYearOptions(startYear),
];

const singleYearCategories = ['operationsCostBasis', 'inputsCostBasis'];

const AlignedDropdown = styled(DropdownButton)`
  margin-left: -0.5rem;
`;

const DashboardExportButtons = () => {
  const { isSuperadmin } = useUserRole();
  const {
    dashboards: { dashboardFarms },
  } = useMultiProjectDashboardContext();

  const [category, setCategory] = useState(overviewCategoryOptions[0]);
  const [projectListFiletype, setProjectListFiletype] = useState(
    projectListFiletypeOptions[0],
  );
  // default to 30 years (last option in multiYearOptions array)
  const [yearOrYears, setYearOrYears] = useState(
    multiYearOptions[multiYearOptions.length - 1],
  );
  const [yearIndex, setYearIndex] = useState(0);
  // TODO: Add more state as needed to represent future dropdowns

  const filteredCategories = useMemo(
    () =>
      isSuperadmin
        ? categoryOptions
        : categoryOptions.filter((option) => !option.superadminOnly),
    [isSuperadmin],
  );

  const startYear = useMemo(
    () => Math.min(...dashboardFarms.map((f) => f.startYear)),
    [dashboardFarms],
  );

  // Reset the selected yearIndex to zero if the earliest startYear changes
  useEffect(() => setYearIndex(0), [startYear]);

  return (
    <>
      <div className="d-flex align-items-center mb-1">
        <AlignedDropdown
          color="light"
          options={filteredCategories}
          selectedOption={category.name}
          setOption={setCategory}
          menuFontSmall
          showOverflow
        />
        {category.value === 'projectList' && (
          <DropdownButton
            className="ml-1"
            color="light"
            options={projectListFiletypeOptions}
            selectedOption={projectListFiletype.name}
            setOption={setProjectListFiletype}
            menuFontSmall
            showOverflow
          />
        )}
        {
          // Revenue, carbon, or yield
          econCategoryOptions
            .map((option) => option.value)
            .includes(category.value) && (
            <DropdownButton
              className="ml-1"
              color="light"
              options={multiOrSingleYearOptions(startYear)}
              selectedOption={yearOrYears.name}
              setOption={setYearOrYears}
              maxMenuHeight="50vh"
              menuFontSmall
            />
          )
        }
        {singleYearCategories.includes(category.value) && (
          <YearSelect
            startYear={startYear}
            yearIndex={yearIndex}
            onChange={setYearIndex}
            className="btn-light ml-2"
            filterStyles={false}
          />
        )}
      </div>
      {category.value === 'projectList' &&
        projectListFiletype.value === 'csv' && <ExportCSVProjectListButton />}
      {category.value === 'crops' && <ExportDashboardCropsButton />}
      {category.value === 'carbon' && (
        <ExportDashboardCarbonButton
          {...yearOrYears.value}
          startYear={startYear}
        />
      )}
      {category.value === 'yield' && (
        <ExportDashboardYieldButton
          {...yearOrYears.value}
          startYear={startYear}
        />
      )}
      {category.value === 'revenue' && (
        <ExportDashboardRevenueButton
          {...yearOrYears.value}
          startYear={startYear}
        />
      )}
      {category.value === 'geneticStockCostBasis' && (
        <ExportDashboardGeneticStockBasisButton />
      )}
      {category.value === 'siteInfraCostBasis' && (
        <ExportDashboardSiteInfrastructureBasisButton />
      )}
      {category.value === 'operationsCostBasis' && (
        <ExportDashboardOperationsBasisButton
          startYear={startYear}
          yearIndex={yearIndex}
        />
      )}
      {category.value === 'inputsCostBasis' && (
        <ExportDashboardInputsBasisButton
          startYear={startYear}
          yearIndex={yearIndex}
        />
      )}
    </>
  );
};

export default DashboardExportButtons;
